import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useIsUserAppointmentOwner } from '@eluve/frontend-appointment-hooks';
import { useAnalytics } from '@eluve/posthog-react';

import {
  BUNDLE_DEFAULT_SENTINEL,
  useAppointmentBundleContext,
} from './AppointmentBundleContext';

interface BundleTemplateSelectorProps {
  usesPortal?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
}

export const BundleTemplateSelector: React.FC<BundleTemplateSelectorProps> = ({
  usesPortal,
  side,
}) => {
  const isUserApptOwner = useIsUserAppointmentOwner();
  const analytics = useAnalytics('bundle-template-selector');
  const toast = useToast();

  const {
    storedBundleId,
    computedDefaultBundleName,
    hasComputedDefault,
    bundles,
    loading,
    updateTemplateBundleId,
    clearToDefault,
  } = useAppointmentBundleContext();

  const selectedBundle = bundles.find((b) => b.id === storedBundleId);

  const triggerLabel = (() => {
    if (loading) return 'Loading Templates...';
    if (storedBundleId === null && hasComputedDefault) {
      return `Default - ${computedDefaultBundleName}`;
    }
    return selectedBundle?.displayName ?? 'Select Template';
  })();

  const handleBundleSelected = async (value: string) => {
    if (value === BUNDLE_DEFAULT_SENTINEL) {
      analytics.capture('appointment.bundle_selected', {
        bundle_id: null,
        bundle_display_name: 'Default',
      });
      try {
        await clearToDefault();
      } catch {
        toast.error('Unable to update template bundle');
      }
      return;
    }

    const bundle = bundles.find((b) => b.id === value);
    if (!bundle) return;

    analytics.capture('appointment.bundle_selected', {
      bundle_id: value,
      bundle_display_name: bundle.displayName,
    });

    try {
      await updateTemplateBundleId(value);
    } catch {
      toast.error('Unable to update template bundle');
    }
  };

  const handleDropdownOpen = (open: boolean) => {
    if (open) {
      analytics.capture('appointment.template_dropdown_clicked', {
        template_count: bundles.length,
        template_type: 'bundle',
      });
    }
  };

  const selectValue =
    storedBundleId === null && hasComputedDefault
      ? BUNDLE_DEFAULT_SENTINEL
      : (storedBundleId ?? '');

  return (
    <Stack gap={2}>
      <Select
        value={selectValue}
        onValueChange={handleBundleSelected}
        onOpenChange={handleDropdownOpen}
        disabled={!isUserApptOwner}
      >
        <SelectTrigger aria-label="Template">{triggerLabel}</SelectTrigger>
        <SelectContent side={side} usesPortal={usesPortal}>
          {hasComputedDefault && (
            <>
              <SelectItem
                className="focus:bg-bg-accent-quaternary"
                value={BUNDLE_DEFAULT_SENTINEL}
              >
                <div className="max-w-96 py-1">
                  <Text>Default - {computedDefaultBundleName}</Text>
                  <Text size="xs" color="supporting">
                    Default for your organization
                  </Text>
                </div>
              </SelectItem>
              <SelectSeparator />
            </>
          )}
          {bundles.map((bundle) => (
            <SelectItem
              key={bundle.id}
              className="focus:bg-bg-accent-quaternary"
              value={bundle.id}
            >
              <div className="max-w-96 py-1">
                <Text>{bundle.displayName}</Text>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Stack>
  );
};
